import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Modal, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import CustomSelect from "../../../components/form/CustomSelect";
import CommonButton from "../../../components/commonButton";
import { fetchActiveCities, fetchActiveStates } from "../../../redux/slice/locationSlice";
import { allUsersList } from "../../../redux/slice/allUsersSlice";
import {
    createMarketplaceRequirement,
    updateMarketplaceRequirement,
    fetchMarketplaceCategoriesPaginated
} from "../../../redux/slice/marketplaceSlice";
import withModal from "../../../components/withModal";
import { useToast } from "../../../context/TostContext";
import InputField from "../../../components/form/InputField";

const STATUS_OPTIONS = [
    { value: "open", label: "Open" },
    { value: "closed", label: "Closed" }
];

function RequirementModal({ onClose, editData = null, onSuccess, isView = false, defaultUser = null }) {
    const dispatch = useDispatch();
    const toast = useToast();
    const [formData, setFormData] = useState({
        user_id: "",
        category_id: "",
        title: "",
        description: "",
        state_id: "",
        city_id: "",
        status: "open"
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (editData) {
            setFormData({
                user_id: editData.user_id || "",
                category_id: editData.category_id || "",
                title: editData.title || "",
                description: editData.description || "",
                state_id: editData.state_id || "",
                city_id: editData.city_id || "",
                status: editData.status || "open"
            });
        } else {
            setFormData({
                user_id: defaultUser?.id || "",
                category_id: "",
                title: "",
                description: "",
                state_id: "",
                city_id: "",
                status: "open"
            });
        }
    }, [editData, defaultUser]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const fetchUserOptionsPaginated = useCallback(async (search, page) => {
        try {
            const result = await dispatch(allUsersList({ search, page, limit: 10 })).unwrap();
            return {
                data: result.data.data,
                meta: result.data.pagination || result.data.meta || {}
            };
        } catch (error) {
            return { data: [], meta: { page: 1, pages: 1 } };
        }
    }, [dispatch]);

    const userDataMapper = useCallback((data) => {
        return (data || []).map(user => ({
            value: user.id,
            label: `${user.first_name || ""} ${user.last_name || ""} (${user.mobile_number || ""})`.trim()
        }));
    }, []);

    const initialUserLabel = useMemo(() => {
        if (editData?.user) {
            const { first_name, last_name, mobile_number } = editData.user;
            return `${first_name || ""} ${last_name || ""} (${mobile_number || ""})`.trim();
        }
        return "";
    }, [editData]);

    const fetchStatesOptions = useCallback((search, page) => {
        return dispatch(fetchActiveStates({ search, page, limit: 20 })).unwrap();
    }, [dispatch]);

    const fetchCitiesOptions = useCallback((search, page) => {
        return dispatch(fetchActiveCities({
            search,
            page,
            limit: 20,
            state_ids: formData.state_id ? [formData.state_id].join(',') : undefined
        })).unwrap();
    }, [dispatch, formData.state_id]);

    const fetchCategoriesOptions = useCallback(async (search, page) => {
        try {
            const result = await dispatch(fetchMarketplaceCategoriesPaginated({ search, page })).unwrap();
            return result;
        } catch (error) {
            return { data: [], meta: { page: 1, pages: 1 } };
        }
    }, [dispatch]);

    const categoryDataMapper = useCallback((data) => {
        return (data || []).map(parent => ({
            label: parent.name,
            options: (parent.subcategories || []).map(sub => ({
                value: sub.id,
                label: sub.name
            }))
        }));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!defaultUser && !formData.user_id) {
            toast.error("Please select a user");
            return;
        }
        if (!formData.title.trim()) {
            toast.error("Please enter a title");
            return;
        }
        if (!formData.category_id) {
            toast.error("Please select a requirement category");
            return;
        }
        if (!formData.state_id) {
            toast.error("Please select a state");
            return;
        }
        if (!formData.city_id) {
            toast.error("Please select a city");
            return;
        }

        setIsSubmitting(true);

        try {
            if (editData) {
                await dispatch(updateMarketplaceRequirement({ id: editData.id, data: formData })).unwrap();
                toast.success("Requirement updated successfully");
            } else {
                await dispatch(createMarketplaceRequirement(formData)).unwrap();
                toast.success("Requirement created successfully");
            }
            onSuccess();
            onClose();
        } catch (error) {
            toast.error(error || "Something went wrong");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit} noValidate>
            <Modal.Body className="grid gap-4">

                {!defaultUser && (
                    <CustomSelect
                        className="g-col-12"
                        label={{ name: "Requester User" }}
                        name="user_id"
                        required
                        isSearchable
                        isClearable
                        value={formData.user_id}
                        onChange={handleSelectChange}
                        fetchOptions={fetchUserOptionsPaginated}
                        dataMapper={userDataMapper}
                        placeholder="Search & Select User (Mobile Number or Name)"
                        initialLabel={initialUserLabel}
                        isDisabled={isView || !!editData}
                    />
                )}

                <InputField className="g-col-6"
                    label={{ name: "Title", }}
                    required={true}
                    input={{
                        name: "title",
                        value: formData.title,
                        onChange: handleInputChange,
                        placeholder: "E.g. Required bulk stainless steel pipes",
                        disabled: isView
                    }} />


                <CustomSelect
                    name="category_id"
                    label={{ name: "Requirement Category" }}
                    required
                    value={formData.category_id}
                    initialLabel={editData?.category?.name || ""}
                    onChange={(name, val) => handleSelectChange(name, val)}
                    fetchOptions={fetchCategoriesOptions}
                    dataMapper={categoryDataMapper}
                    placeholder="Select Category"
                    isDisabled={isView}
                    className="g-col-6"
                />



                <InputField className="g-col-12"
                    label={{ name: "Description", }}

                    input={{
                        name: "description",
                        as: "textarea",
                        value: formData.description,
                        onChange: handleInputChange,
                        placeholder: "Tell more about the requirement...",
                        rows: 4,
                        disabled: isView
                    }} />
                <CustomSelect
                    name="state_id"
                    label={{ name: "State" }}
                    value={formData.state_id}
                    initialLabel={editData?.state?.name || ""}
                    onChange={(name, val) => {
                        handleSelectChange(name, val);
                        handleSelectChange("city_id", "");
                    }}
                    fetchOptions={fetchStatesOptions}
                    placeholder="Search State"
                    required
                    isDisabled={isView}
                    className="g-col-6"
                />

                <CustomSelect
                    className="g-col-6"
                    name="city_id"
                    label={{ name: "City" }}
                    value={formData.city_id}
                    initialLabel={editData?.city?.name || ""}
                    onChange={handleSelectChange}
                    fetchOptions={fetchCitiesOptions}
                    placeholder="Search City"
                    required
                    isDisabled={!formData.state_id || isView}
                />

                {editData && (
                    <div className="g-col-6">
                        <CustomSelect
                            label={{ name: "Status", required: true }}
                            options={STATUS_OPTIONS}
                            value={STATUS_OPTIONS.find(o => o.value === formData.status)}
                            onChange={(opt) => handleSelectChange("status", opt?.value)}
                            isDisabled={isView}
                        />
                    </div>
                )}



            </Modal.Body>
            <Modal.Footer>
                <CommonButton
                    title={isView ? "Close" : "Cancel"}
                    variant="outline"
                    onClick={onClose}
                    type="button"
                />
                {!isView && (
                    <CommonButton
                        title={editData ? "Update Requirement" : "Create Requirement"}
                        variant="primary"
                        type="submit"
                        isLoading={isSubmitting}
                    />
                )}
            </Modal.Footer>
        </Form>
    );
}

export default withModal(RequirementModal);
